import {useState, useEffect, useCallback, type ReactNode, type CSSProperties} from 'react'
import LoadingLogo from './LoadingLogo'
import {useNavigate} from 'react-router-dom'
import {useAppDispatch, store} from '../../common/store'
import {applyDeviceConfig, loadProject} from '../../common/components/store/scenesSlice'
import type {DeviceConfig, SceneFullConfig} from '../../common/components/types'
import {EditorConfigContext} from '../../common/hooks/useEditorConfig'
import {getProjectDefaults} from '../../common/api/generated/endpoints/defaults/defaults'
import {triggerCameraPreset} from '../../common/components/deviceMesh/cameraPresetStore'
import {serializeConfig} from './topMenuBar/serializeConfig'
import {setSavedSnapshot} from '../../common/components/store/savedSnapshotStore'
import {stableStringify} from '../../common/utils/stableStringify'
import {
  isStateOwnedByFullEditor,
  markStateOwnedByCasual,
  markStateOwnedByFullEditor,
} from '../../common/components/store/editorScopeStore'

// ── Provider ─────────────────────────────────────────────────────

type LoadState =
  | {status: 'loading'}
  | {status: 'ready'; deviceConfig: DeviceConfig; defaultDeviceConfig: DeviceConfig}
  | {status: 'error'}

interface EditorConfigProviderProps {
  children: ReactNode
  // When true (default), navigates to /error on load failure. Disable for embedded usage.
  redirectOnError?: boolean
  // Rendered in place of children while loading or after an error.
  fallback?: ReactNode
  /**
   * Scope of this provider.
   * - 'casual' (default): landing embedded editor, /free standalone — preserve
   *   existing Redux state when navigating between them.
   * - 'editor': full editor (/editor/*) — its loaded project state must not leak
   *   into casual surfaces on subsequent navigation.
   */
  scope?: 'casual' | 'editor'
}

export const EditorConfigProvider = ({
  children,
  redirectOnError = true,
  fallback,
  scope = 'casual',
}: EditorConfigProviderProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [loadState, setLoadState] = useState<LoadState>({status: 'loading'})

  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const {data} = await getProjectDefaults()
        const firstConfig = data.scenes[0]?.config as Record<string, unknown> | undefined
        const deviceConfig = firstConfig?.['device'] as DeviceConfig | undefined
        if (!deviceConfig) throw new Error('Defaults missing device config')

        // Reuse existing redux state when it belongs to this scope (e.g. user
        // navigated from landing's embedded editor to /free — both casual).
        // The full editor never reuses: each /editor URL must start from defaults
        // so route transitions can't carry the previous project's scenes across.
        // (EditorRoute additionally keys on pathname to force a remount, but the
        // reuse guard here is what makes the new-project route truly fresh even
        // when react-router happens to keep the component mounted.)
        const presentState = store.getState().present
        const hasExistingState = presentState.scenes.scenes.length > 0
        const stateOwnedByOtherScope = scope === 'casual' && isStateOwnedByFullEditor()
        const shouldReuse = scope === 'casual' && hasExistingState && !stateOwnedByOtherScope
        if (!shouldReuse) {
          const scenes = data.scenes.map((s) => s.config as unknown as SceneFullConfig)
          const sceneNames = data.scenes.map((s) => s.name)
          dispatch(loadProject({scenes, deviceConfig, sceneNames}))
          // Reset camera preset store so CameraSync applies the default position on mount
          // (prevents stale camera from a previously opened project leaking through)
          const camera = firstConfig?.['camera'] as {position: number[]; rotation: number[]} | undefined
          if (camera) triggerCameraPreset(camera.position, camera.rotation)
          // Set initial snapshot for leave guard (may be overwritten by loadProject/applyScenePreset)
          setSavedSnapshot(stableStringify(serializeConfig(store.getState().present, deviceConfig)))
        } else {
          // Re-bump the camera preset store with the preserved camera so the
          // freshly-mounted CameraSync applies the saved position (not stale defaults).
          const activeScene = presentState.scenes.scenes[presentState.scenes.activeSceneIndex]
          triggerCameraPreset(activeScene.cameraPosition, activeScene.cameraRotation)
        }
        if (scope === 'editor') markStateOwnedByFullEditor()
        else markStateOwnedByCasual()
        setLoadState({status: 'ready', deviceConfig, defaultDeviceConfig: deviceConfig})
      } catch {
        setLoadState({status: 'error'})
      }
    }

    fetchDefaults()
  }, [dispatch, scope])

  // Navigate to error page on fatal load failure (skipped when embedded)
  useEffect(() => {
    if (loadState.status === 'error' && redirectOnError) {
      navigate('/error', {replace: true})
    }
  }, [loadState.status, navigate, redirectOnError])

  const setDeviceConfig = useCallback(
    (config: DeviceConfig) => {
      dispatch(applyDeviceConfig(config))
      setLoadState((prev) => {
        const defaultDc = prev.status === 'ready' ? prev.defaultDeviceConfig : config
        return {status: 'ready', deviceConfig: config, defaultDeviceConfig: defaultDc}
      })
    },
    [dispatch],
  )

  if (loadState.status !== 'ready') {
    if (fallback !== undefined) return <>{fallback}</>
    return (
      <div style={styles.loader}>
        <LoadingLogo size={48} />
      </div>
    )
  }

  return (
    <EditorConfigContext.Provider
      value={{
        deviceConfig: loadState.deviceConfig,
        defaultDeviceConfig: loadState.defaultDeviceConfig,
        setDeviceConfig,
      }}
    >
      {children}
    </EditorConfigContext.Provider>
  )
}

const styles: Record<string, CSSProperties> = {
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100dvh',
  },
}
